import { SelectChangeEvent } from '@mui/material';
import { useAppDispatch } from 'app/hooks';
import { FETCH_ALL_PARAMS } from 'constants/query';
import { useGetNestedProductCategoriesQuery } from 'features/nested-product-category/services';
import { useGetNestedProductsQuery } from 'features/nested-product/services';
import { useGetPlacesQuery } from 'features/place/services';
import { useGetProductCategoriesQuery } from 'features/product-category/services';
import { useForm, ValidationErrorsType, ValidationErrorType } from 'hooks/useForm';
import { useRole } from 'hooks/useRole';
import { useWorkspace } from 'hooks/useWorkspace';
import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useAddDocumentMutation, useDeleteDocumentMutation } from 'services/documents';
import { setErrorMessage, setSuccessMessage } from 'store/toast-slice';
import { PlaceConfigData } from 'types/dtos';
import { ServerErrorType } from 'types/server-responses';
import { dataURLtoFile } from 'utils/file-helpers';
import { isEmpty, removeEmpty, trimValues } from 'utils/object-helpers';
import { handleConfigFieldChange } from 'utils/products-config-price-helper';
import { validateConfigFields } from 'utils/products-config-validation-helper';
import { PRODUCT_FIELDS_SCHEMA, PRODUCT_INITIAL_FIELDS } from '../constants/product-fields';
import { useAddProductMutation } from '../services';
import { ProductDto } from '../types/dtos';
import useToggle from 'hooks/useToggle';
import { PRODUCT_STATUS } from '../constants/status';

type AllErrorsType<T> = ValidationErrorsType<T> & {
    [key: string]: ValidationErrorType | undefined;
};

const useController = () => {
    const { placeId = '' } = useParams();
    const [individualPrices, setIndividualPrices] = useState(false);
    const [placeConfigData, setPlaceConfigData] = useState<PlaceConfigData[]>([]);
    const [priceErrors, setPriceErrors] = useState<Record<string, ValidationErrorType>>({});
    const [selectedNestedProducts, setSelectedNestedProducts] = useState<string[]>([]);
    const { isAdmin } = useRole();
    const { websiteId } = useWorkspace();
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const navigate = useNavigate();
    const [addDocument, { isLoading: isAddDocumentLoading }] = useAddDocumentMutation();
    const [deleteDocument, { isLoading: isDeleteDocumentLoading }] = useDeleteDocumentMutation();
    const [addProduct, { isLoading: isAddProductLoading }] = useAddProductMutation();
    const { data: places, isLoading: isGetPlacesLoading } = useGetPlacesQuery({
        ...FETCH_ALL_PARAMS,
        website_id: websiteId,
    });
    const [isSelectedNestedProductsDialogOpen, toggleSelectedNestedProductsDialog] = useToggle(false);

    const { data: productCategories, isLoading: isGetProductCategoriesLoading } =
        useGetProductCategoriesQuery(FETCH_ALL_PARAMS);

    const { data: nesteProductCategories, isSuccess: isSuccessNestedProductCategories } =
        useGetNestedProductCategoriesQuery({
            website_id: websiteId,
            ...FETCH_ALL_PARAMS,
        });
    const { data: nestedProducts, isSuccess: isSuccessNestedProducts } = useGetNestedProductsQuery({
        website_id: websiteId,
        ...FETCH_ALL_PARAMS,
    });

    const nestedCategoriesWithNestedProducts = useMemo(() => {
        if (!isSuccessNestedProductCategories || !isSuccessNestedProducts) return [];

        const categoriesWithProducts = nesteProductCategories.content.map((category) => {
            const products = nestedProducts.content.filter(
                (product) => product.nestedProductCategoryId === category.id,
            );
            return {
                ...category,
                products,
            };
        });

        return categoriesWithProducts;
    }, [isSuccessNestedProductCategories, isSuccessNestedProducts, nesteProductCategories, nestedProducts]);

    const {
        formData,
        handleChange: onChange,
        handleSubmit,
        errors,
    } = useForm({ defaultValues: PRODUCT_INITIAL_FIELDS, validationSchema: PRODUCT_FIELDS_SCHEMA });

    const allErrors: AllErrorsType<typeof PRODUCT_INITIAL_FIELDS> = useMemo(() => {
        return {
            ...errors,
            ...priceErrors,
        };
    }, [errors, priceErrors]);

    useEffect(() => {
        if (places?.content && placeId) {
            const initialPlace = places.content.find((place) => place.id === placeId);
            if (initialPlace) {
                setPlaceConfigData([
                    {
                        placeId: initialPlace.id,
                        placeName: initialPlace.name,
                        price: '',
                        discountedPrice: '',
                        isAvailable: `${PRODUCT_STATUS.AVAILABLE}`,
                    },
                ]);
            }
        }
    }, [places, placeId]);

    const handlePlaceConfigSelectChange = useCallback(
        (event: SelectChangeEvent<string[]>) => {
            const {
                target: { value },
            } = event;
            const selectedIds = typeof value === 'string' ? value.split(',') : value;

            if (places?.content) {
                const newPlaceProductConfig = places.content
                    .filter((place) => selectedIds.includes(place.id))
                    .map((place) => {
                        const existing = placeConfigData.find((config) => config.placeId === place.id);
                        return (
                            existing || {
                                placeId: place.id,
                                placeName: place.name,
                                price: '',
                                discountedPrice: '',
                                isAvailable: `${PRODUCT_STATUS.AVAILABLE}`,
                            }
                        );
                    });

                setPlaceConfigData(newPlaceProductConfig);
            }
        },
        [places?.content, placeConfigData, setPlaceConfigData],
    );

    const validatePrices = useCallback(
        (configs: PlaceConfigData[]): Record<string, ValidationErrorType> => {
            return validateConfigFields<PlaceConfigData>(configs, 'placeId', ['price', 'discountedPrice'], {
                price: {
                    required: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_REQUIRED'),
                    },
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.PRICE_POSITIVE'),
                    },
                },
                discountedPrice: {
                    isNumber: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_NUMBER'),
                    },
                    isPositive: {
                        message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_POSITIVE'),
                    },
                    custom: [
                        {
                            validator: (value, allValues) =>
                                !allValues.price.trim() || Number(value) < Number(allValues.price),
                            message: t('PRODUCT_FORM_VALIDATIONS.DISCOUNTED_PRICE_LOWER_THAN_PRICE'),
                        },
                    ],
                },
            });
        },
        [t],
    );

    const handlePlaceConfigPriceChange = useCallback(
        (field: string, value: string, placeId?: string) => {
            handleConfigFieldChange({
                field,
                value,
                id: placeId,
                currentData: placeConfigData,
                setData: setPlaceConfigData,
                errorState: priceErrors,
                setErrorState: setPriceErrors,
                validateFn: validatePrices,
                errorPrefixes: ['price-', 'discountedPrice-'],
            });
        },
        [placeConfigData, priceErrors, setPlaceConfigData, setPriceErrors, validatePrices],
    );

    const handleSuccess = useCallback(() => {
        dispatch(setSuccessMessage({ key: 'product.PRODUCT_ADDED' }));
    }, [dispatch]);

    const handleErrors = useCallback(
        (error: ServerErrorType) => {
            const code = error.data?.errorCode;
            dispatch(setErrorMessage({ key: code }));
        },
        [dispatch],
    );

    const getPayload = useCallback(
        (documentId?: string) => {
            const data = { ...formData };
            if (documentId) data.documentId = documentId;
            delete data.logo;
            const placeConfigsPayload = placeConfigData.map((config) => {
                const { placeId, isAvailable, price, discountedPrice } = config;
                return {
                    placeId,
                    price: Number(price),
                    discountedPrice: discountedPrice ? Number(discountedPrice) : null,
                    isAvailable: isAvailable === `${PRODUCT_STATUS.AVAILABLE}` ? true : false,
                };
            });
            data.placeConfigs = placeConfigsPayload;
            data.websiteId = websiteId;

            return trimValues(removeEmpty(data)) as ProductDto;
        },
        [formData, placeConfigData, websiteId],
    );

    const onSubmit = useCallback(
        (e: FormEvent<HTMLFormElement>) => {
            e.preventDefault();
            handleSubmit(async () => {
                let documentId: string | undefined;

                try {
                    if (formData?.logo)
                        documentId = (await addDocument(dataURLtoFile(formData.logo)).unwrap()).documentId;

                    const errorsSubmit = validatePrices(placeConfigData);

                    if (!isEmpty(errorsSubmit)) {
                        setPriceErrors((prev) => {
                            return { ...prev, ...errorsSubmit };
                        });
                        return;
                    }

                    const payload = getPayload(documentId);

                    await addProduct({ ...payload, nestedProductIds: selectedNestedProducts }).unwrap();

                    handleSuccess();
                    navigate(-1);
                } catch (error) {
                    documentId && (await deleteDocument(documentId).unwrap());
                    handleErrors(error as ServerErrorType);
                }
            });
        },
        [
            addProduct,
            addDocument,
            deleteDocument,
            formData?.logo,
            getPayload,
            handleErrors,
            handleSubmit,
            handleSuccess,
            navigate,
            selectedNestedProducts,
            validatePrices,
            placeConfigData,
        ],
    );

    const onCancel = useCallback(() => {
        navigate(-1);
    }, [navigate]);

    const onSelectNestedProductComplete = useCallback((selectedProducts: string[]) => {
        setSelectedNestedProducts(selectedProducts);
    }, []);

    return {
        formData,
        places,
        productCategories,
        onSubmit,
        onCancel,
        onChange,
        isLoading: isGetPlacesLoading || isGetProductCategoriesLoading,
        isAddProductLoading: isAddProductLoading || isAddDocumentLoading || isDeleteDocumentLoading,
        errors: allErrors,
        isAdmin,
        websiteId,
        onSelectConfigChange: handlePlaceConfigSelectChange,
        individualPrices,
        setIndividualPrices,
        placeConfigData,
        onConfigPriceChange: handlePlaceConfigPriceChange,
        placeId,
        nestedCategoriesWithNestedProducts,
        isSelectedNestedProductsDialogOpen,
        toggleSelectedNestedProductsDialog,
        onSelectNestedProductComplete,
        selectedNestedProducts,
    };
};

export default useController;
